<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/api';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { isAxiosError } from 'axios';
import AdminBaseTable, { Field } from '@/components/admin/admin-base-table.vue';
import ActionBalloon from '@/components/common/molecules/balloon.vue';
import { useSnackbar } from '@/components/common/snackbar/use-snackbar';
import UserDocument from '@/components/layouts/user-document.vue';
import { formatDateForStorage } from '@/utils/formatters';
import { checkMoveToAdmin } from '@/utils/user';
import { useBox } from '@/utils/user-document/box';
import {
  AdminTableItem,
  displayStorageStatus,
  displaySyncStatus,
  isConnectedFailed,
  isSynced,
  isSyncing,
  isUncertified,
  showDisconnectModal,
} from '@/utils/user-document/storage';
import { useStore } from '@/utils/vue';

const router = useRouter();
const route = useRoute();
const store = useStore();
const { createSnackbar } = useSnackbar();
const {
  connect: connectBox,
  disconnect: disconnectBox,
  updateDetails: updateDetailsBox,
  reauthorize: reauthorizeBox,
} = useBox();

const actionMenuItem = ref<AdminTableItem | undefined>(undefined);
const adminTableItem = ref<AdminTableItem>({
  id: 1,
  storageType: 'box',
  isLoaded: false,
  isConnected: false,
  syncStatus: '-',
  fileCount: '-',
  syncStartedAt: '-',
  syncEndedAt: '-',
  connectText: 'ストレージを認証',
  status: 'NOT_CONNECTED',
});

const fields = computed<Field<AdminTableItem>[]>(() => [
  {
    name: 'isConnected',
    displayName: 'ストレージ状況',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncStatus',
    displayName: 'ステータス',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'fileCount',
    displayName: '同期済みのファイル数',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncStartedAt',
    displayName: '同期開始時刻',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'syncEndedAt',
    displayName: '同期終了時刻',
    isSortable: false,
    type: 'string',
    fieldPosition: 'left',
    cellPosition: 'left',
  },
  {
    name: 'actions',
    displayName: '',
    isSortable: false,
    type: 'string',
    fieldPosition: 'right',
    cellPosition: 'right',
  },
]);
// 連携状態を更新
const updateStorageStatus = () => {
  updateDetailsBox(adminTableItem.value);
};

const connectHandler = () => {
  router.push({ name: 'boxFolderCreate' });
};

const disconnectHandler = (item: AdminTableItem) => {
  const action = disconnectBox(item);
  showDisconnectModal(store, item, action);
};

const clickMenu = (item: AdminTableItem) => {
  actionMenuItem.value =
    actionMenuItem.value?.id === item.id ? undefined : item;
};

const editBoxFolderHandler = () => {
  router.push({ name: 'boxFolders' });
};

const reauthorizeHandler = async (item: AdminTableItem) => {
  try {
    await reauthorizeBox(item);
  } catch (e) {
    const message =
      isAxiosError(e) && e.response?.status === 409
        ? '同期処理中のため再認証できません。しばらく経ってから再度お試しください。'
        : '再認証の開始に失敗しました。再度お試しください。';
    createSnackbar({ message, type: 'error' });
  }
};

const actions: {
  name: string;
  func: (item: AdminTableItem) => void;
}[] = [
  {
    name: 'ストレージの認証解除',
    func: disconnectHandler,
  },
  {
    name: 'BOXのフォルダ情報',
    func: editBoxFolderHandler,
  },
  {
    name: 'BOXの再認証',
    func: reauthorizeHandler,
  },
];

onMounted(async () => {
  await checkMoveToAdmin(router);
  const teamInfo = await api.fetchTeamInfo();

  if (!teamInfo.enable_user_document) {
    createSnackbar({
      message: '権限がありません',
      type: 'error',
    });
    await router.push({ name: 'anewsHome' });
  }
  // BOXの認証などで、リダイレクトでエラーを表示させる場合
  const { error, from, ...query } = route.query;
  if (error === 'box_validation_error') {
    createSnackbar({
      message: '認証情報の登録に失敗しました',
      type: 'error',
    });
    // リロードの時、エラーを表示しないようにする
    await router.replace({ query });
  }

  updateStorageStatus();
  // BOXフォルダ登録画面から戻ってきた場合
  if (from === 'box_folder_create') {
    await router.replace({ query });
    connectBox(adminTableItem.value);
  }
});
</script>

<template>
  <UserDocument>
    <div class="c-title-bold--m">BOX</div>
    <div class="container">
      <AdminBaseTable
        :is-stricky-header="true"
        :fields="fields"
        :items="[adminTableItem]"
        action-width="240px"
      >
        <template #header-fileCount="{ item }">
          <div class="header-title-with-icon">
            <div>{{ item.displayName }}</div>
            <DgrIcon
              size="small"
              name="question-circle"
              v-tooltip.top="{
                content:
                  '連携中のファイルはPDF/Word/PowerPoint/Excelのみとなります',
                theme: 'menu',
              }"
            />
          </div>
        </template>
        <template #cell-isConnected="{ item }">
          <span v-text="displayStorageStatus(item)"></span>
        </template>
        <template #cell-syncStatus="{ item }">
          <DgrIcon
            :keep-fill="false"
            name="chart-round-bubble"
            v-if="isSyncing(item.status)"
            class="name-icon syncing"
          />
          <DgrIcon
            :keep-fill="false"
            name="check"
            v-else-if="isSynced(item.status)"
            class="name-icon synced"
          />
          <span v-text="displaySyncStatus(item)"></span>
        </template>
        <template #cell-fileCount="{ item }">
          <span v-text="item.fileCount"></span>
        </template>
        <template #cell-syncStartedAt="{ item }">
          <span v-text="formatDateForStorage(item.syncStartedAt)"></span>
        </template>
        <template #cell-syncEndedAt="{ item }">
          <span v-text="formatDateForStorage(item.syncEndedAt)"></span>
        </template>
        <template #cell-actions="{ item }">
          <div v-if="isUncertified(item)">
            <div class="action-connect actions">
              <div class="action-buttons">
                <button
                  class="action-button c-outlineBtn c-btn--auto c-btn--small c-outlineBtn--secondary"
                  :class="{ disabled: !item.isLoaded }"
                  :disabled="!item.isLoaded"
                  @click="connectHandler()"
                  v-text="item.connectText"
                ></button>
              </div>
              <div class="connect-failed" v-if="isConnectedFailed(item.status)">
                認証情報の取得に失敗しました。再度やり直してください。
              </div>
            </div>
          </div>
          <div v-else>
            <div class="actions">
              <div
                data-testid="action-icon"
                class="action-icon"
                @click.stop="clickMenu(item)"
              >
                <DgrIcon name="ellipsis-h" />
              </div>
              <div class="action-menu">
                <ActionBalloon
                  position="absolute"
                  :is-show="actionMenuItem?.id === item.id"
                  balloon-position="right"
                  @on-click-outside="actionMenuItem = undefined"
                >
                  <div v-for="action in actions" :key="action.name">
                    <div
                      class="item c-text-regular--m"
                      @click="
                        action.func ? action.func(item) : undefined;
                        actionMenuItem = undefined;
                      "
                    >
                      {{ action.name }}
                    </div>
                  </div>
                </ActionBalloon>
              </div>
            </div>
          </div>
        </template>
      </AdminBaseTable>
    </div>
  </UserDocument>
</template>

<style scoped lang="scss">
.container {
  margin-top: 16px;

  .header-title-with-icon {
    display: flex;
    gap: 8px;
    flex-wrap: nowrap;
    align-items: center;
  }

  .name-icon {
    display: inline-block;
    margin-right: 10px;
    vertical-align: middle;

    &.syncing {
      color: $color-gray800;
    }
    &.synced {
      color: $color-green600;
    }
    &.sync-failed {
      color: $color-orange1000;
    }
  }

  .action-icon {
    display: flex;
    justify-content: center;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;

    &:hover {
      background-color: $color-gray200;
    }
  }

  .action-buttons {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-end;
  }

  .action-button {
    white-space: nowrap;
    margin-left: auto;
  }

  .connect-failed {
    text-align: right;
    color: $color-orange1000;
  }

  .actions {
    display: inline-block;
  }

  .item {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 4px 12px;
    &:hover {
      background: $color-gray400;
    }
    &.disabled {
      color: $color-gray600;
    }
    .icon {
      margin-right: 4px;
    }
  }
}
</style>
